/*
 * WARNING: This file is automatically maintained by the `yarn generate` command.
 * Changes must be made to the package.templateroot, or they will be lost!
 */
import { getDefaultClient, IRequestOptions, IRetryParams, ITimeoutParams } from '@ifit/gateway-core';
import type * as Axios from 'axios';
import type * as ReactQuery from 'react-query';
import type { IStationaryConsoleGetParameters } from '../models/stationary-console-get-parameters';
import type { IStationaryConsoleGetResponseOK } from '../models/stationary-console-get-response-ok';
import { consoleStoreIsDown } from './is-down';

type Params = IStationaryConsoleGetParameters & IRequestOptions & (ITimeoutParams | IRetryParams);

let setMock: (params: Params & { returnResponse?: boolean }) => Promise<IStationaryConsoleGetResponseOK | Axios.AxiosResponse<IStationaryConsoleGetResponseOK>>;
let reactQuery: typeof ReactQuery;

export function stationaryConsoleGetSetMock(mock: (params: Params & { returnResponse?: boolean }) => Promise<IStationaryConsoleGetResponseOK | Axios.AxiosResponse<IStationaryConsoleGetResponseOK>>) {
  setMock = mock;
}

export async function stationaryConsoleGet(params: Params & { returnResponse?: false }):
  Promise<IStationaryConsoleGetResponseOK>;
export async function stationaryConsoleGet(params: Params & { returnResponse: true }):
  Promise<Axios.AxiosResponse<IStationaryConsoleGetResponseOK>>;
export async function stationaryConsoleGet(params: Params & { returnResponse?: boolean }):
  Promise<IStationaryConsoleGetResponseOK | Axios.AxiosResponse<IStationaryConsoleGetResponseOK>> {
  if (!params) {
    params = {} as any;
  }
  if (setMock) {
    return setMock(params);
  }
  const client = params.client || getDefaultClient();
  if (!client) {
    throw new Error('No @ifit/gateway-core GatewayApiClient has been constructed yet! Please do so before calling gateway methods, or pass a compatible client in the parameters.');
  }
  const requestURL = `/console-store/v1/stationary-console/${ params.id }`;
  const requestConfig = client.getRequestConfig(params);
  
  const headers: any = { ...requestConfig.headers, ...params.headers };
  const queryParams: any = { ...requestConfig.params };
  queryParams.select = params.select;
  queryParams.withEquipmentLimits = params.withEquipmentLimits;

  if ((client.statusChecksEnabled || params.forceStatusCheck) && (await consoleStoreIsDown(params))) {
    return null;
  }

  const response = await client.req.send<IStationaryConsoleGetResponseOK>(requestURL, {
    ...requestConfig,
    headers,
    method: 'GET',
    params: queryParams,
  }, params);
  return params.returnResponse
    ? response
    : response.data;
}

export const useKeyStationaryConsoleGet = 'console-store:stationaryConsoleGet:get';

export function useQueryStationaryConsoleGet(
  requestParams: Params,
  queryOptions?: ReactQuery.UseQueryOptions<IStationaryConsoleGetResponseOK, Axios.AxiosError<unknown>, IStationaryConsoleGetResponseOK, [string, Params]>
): ReactQuery.UseQueryResult<IStationaryConsoleGetResponseOK, Axios.AxiosError<unknown>> {
  if (!reactQuery) {
    reactQuery = require('react-query');
  }
  const key: [string, Params] = [ useKeyStationaryConsoleGet, requestParams ];
  return reactQuery.useQuery(key, queryFn, queryOptions);
}

export function useInfiniteQueryStationaryConsoleGet(
  requestParams: Params,
  queryOptions: ReactQuery.UseInfiniteQueryOptions<IStationaryConsoleGetResponseOK, Axios.AxiosError<unknown>, IStationaryConsoleGetResponseOK, IStationaryConsoleGetResponseOK, [string, Params]>
): ReactQuery.UseInfiniteQueryResult<IStationaryConsoleGetResponseOK, Axios.AxiosError<unknown>> {
  if (!reactQuery) {
    reactQuery = require('react-query');
  }
  const key: [string, Params] = [ useKeyStationaryConsoleGet, requestParams ];
  return reactQuery.useInfiniteQuery(key, queryFn, queryOptions);
}

function queryFn({ queryKey, pageParam }: { queryKey: [string, Params], pageParam?: any }) {
  const [, params] = queryKey;
  const promise = stationaryConsoleGet(pageParam ? { ...params, ...pageParam } : params) as Promise<IStationaryConsoleGetResponseOK> & { cancel?: () => void; };
  if (params?.cancelSource) {
    promise.cancel = () => {
      params.cancelSource.cancel('Query was cancelled by React Query');
    };
  }
  return promise;
}
